import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { apiRequest, readStoredToken, storeToken } from "../../shared/api/client";
import type { AuthUser, LoginResponse } from "./types";

type AuthStatus = "loading" | "authenticated" | "guest";

type AuthContextValue = {
  status: AuthStatus;
  token: string;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState(readStoredToken);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>(token ? "loading" : "guest");

  const clearSession = useCallback(() => {
    storeToken("");
    setToken("");
    setUser(null);
    setStatus("guest");
  }, []);

  const refreshSession = useCallback(async () => {
    if (!token) {
      clearSession();
      return;
    }
    setStatus("loading");
    try {
      const currentUser = await apiRequest<AuthUser>("/v1/auth/me", token);
      if (currentUser.role !== "ADMIN" && currentUser.role !== "SUPERADMIN") {
        throw new Error("Nieobsługiwana rola użytkownika");
      }
      setUser(currentUser);
      setStatus("authenticated");
    } catch {
      clearSession();
    }
  }, [clearSession, token]);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const login = useCallback(async (email: string, password: string) => {
    const payload = await apiRequest<LoginResponse>("/v1/auth/login", "", {
      method: "POST",
      body: { email: email.trim(), password }
    });
    if (!payload.accessToken || !payload.user) throw new Error("Niepełna odpowiedź logowania");
    storeToken(payload.accessToken);
    setToken(payload.accessToken);
    setUser(payload.user);
    setStatus("authenticated");
    return payload.user;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, token, user, login, logout: clearSession, refreshSession }),
    [clearSession, login, refreshSession, status, token, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
